import {
  EyeIcon,
  FireIcon,
  InteractionsHandIcon,
  LikeIcon,
  NewPlayIcon,
} from '~/components/icons';
import { STORY_ASPECT_RATIO } from '~/constants';
import {
  Content,
  CreatorBenchmarks,
  MediaPackContent,
  PostContent,
  ReelsContent,
  StoryContent,
  TiktokType,
  YoutubeType,
} from '~/types';
import { formatAbbr, formatInteger, formatPercentage } from '~/utils';

import { getContentLink } from '../../MediaPackDetailDefaultScreen.helpers';
import { PLATFORM_METRICS } from '../MediaPackDetailEditHighlightsModal/MediaPackDetailEditHighlightsModal.constants';
import { HighlightedContentProps } from '../MediaPacksDefaultTemplate/HighlightedContentSection/HighlightedContent';
import { mapMetricsToMetricCardData } from '../MediaPacksDefaultTemplate/OverviewSection/OverviewSection.mapper';
import { POST_ASPECT_RATIO } from './InstagramSection.constants';

interface PostsProps {
  hasManager?: boolean;
  igPosts: PostContent[];
  isSharedView?: boolean;
}

interface StoriesProps {
  hasManager?: boolean;
  igStories: StoryContent[];
  isSharedView?: boolean;
}

interface ReelsProps {
  hasManager?: boolean;
  igReels: ReelsContent[];
  isSharedView?: boolean;
}

export const mediaPackContentToIds = (
  contents: MediaPackContent[] | Content[] | TiktokType[] | YoutubeType[] = [],
): string[] => contents.map((content) => content.id);

export const mapInstagramMetricsToMetricCardData = (
  metrics: string[],
  creatorBenchmarks: CreatorBenchmarks,
) =>
  mapMetricsToMetricCardData(
    metrics,
    PLATFORM_METRICS.instagram,
    creatorBenchmarks,
  );

export const mapPostsToHighlightedContentProps = ({
  hasManager,
  igPosts,
  isSharedView,
}: PostsProps): HighlightedContentProps[] =>
  igPosts.map((post) => ({
    aspectRatio: POST_ASPECT_RATIO,
    contentFooter: [
      { icon: LikeIcon, label: `${formatAbbr(post.likes)} Engagements` },
      {
        icon: FireIcon,
        label: `${formatPercentage(
          post.postReachEngRate,
        )} Reach Engagement Rate`,
      },
    ],
    contentId: post.id,
    link: getContentLink({
      hasManager,
      id: post.id,
      isSharedView,
      shareLink: post.shareLink,
    }),
    contentSrc: post?.assets?.[0]?.previewUrl ?? '',
  }));

export const mapStoriesToHighlightedContentProps = ({
  hasManager,
  igStories,
  isSharedView,
}: StoriesProps) =>
  igStories.map((story) => ({
    aspectRatio: STORY_ASPECT_RATIO,
    contentFooter: [
      {
        icon: EyeIcon,
        label: `${formatAbbr(story.impressions)} Impressions`,
      },
      { icon: FireIcon, label: `${formatAbbr(story.reach)} Reach` },
    ],
    contentId: story.id,
    link: getContentLink({
      hasManager,
      id: story.id,
      isSharedView,
      shareLink: story.shareLink,
    }),
    contentSrc: story?.assets?.[0]?.previewUrl,
  }));

export const mapReelsToHighlightedContentProps = ({
  hasManager,
  igReels,
  isSharedView,
}: ReelsProps) =>
  igReels.map((reel) => ({
    aspectRatio: STORY_ASPECT_RATIO,
    contentFooter: [
      {
        icon: EyeIcon,
        label: `${formatPercentage(reel.videoReachViewRate)} VER`,
      },
      {
        icon: NewPlayIcon,
        label: `${formatInteger(
          reel.igReelsAggregatedAllPlaysCount ?? reel.plays,
        )} Views`,
      },
      {
        icon: InteractionsHandIcon,
        label: `${formatInteger(reel.interactions)} Engagements`,
      },
    ],
    contentId: reel.id,
    link: getContentLink({
      hasManager,
      id: reel.id,
      isSharedView,
      shareLink: reel.shareLink,
    }),
    contentSrc: reel?.assets?.[0]?.previewUrl,
  }));
